'use client';

import Container from '@mui/material/Container';
import { type PropsWithChildren, createContext, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useSession } from 'next-auth/react';
import Box from '@mui/material/Box';

import { theme } from '@ifa/ui/theme';
import { useIfaStore } from '@ifa/store';
import Drawer from '@ifa/ui/details/Drawer';

import { env } from '../../env';

import ResponsiveAppBar from './ResponsiveAppBar';
import Copyright from './Copyright';

const DRAWER_WIDTH = '450px';

export const EnvContext = createContext({ version: '' });

export default function AppLayout({ children }: PropsWithChildren) {
    const closeDrawer = useIfaStore(state => state.drawer.closeDrawer);
    const drawerStatus = useIfaStore(state => state.drawer.opened);
    const { data: session } = useSession();
    const pathname = usePathname();

    useEffect(() => {
        // change this in case the Drawer is used in other places besides /dashboard
        if (!pathname.includes('dashboard')) {
            closeDrawer();
        }
    }, [pathname, closeDrawer]);

    return (
        <EnvContext.Provider value={{ version: env.NEXT_PUBLIC_APP_VERSION }}>
            <ResponsiveAppBar />
            <Box
                sx={{
                    width: '100%',
                    height: 'calc(100vh - 64px)',
                    position: 'relative',
                    overflow: 'hidden',
                    marginTop: '64px',
                }}
            >
                <Box
                    sx={{
                        width: session ? `calc(100% + ${DRAWER_WIDTH})` : '100%',
                        justifyContent: session ? 'start' : 'center',
                        display: 'flex',
                        height: '100%',
                    }}
                >
                    {/** Content Container */}
                    <Box
                        sx={{
                            flex: drawerStatus
                                ? `0 0 calc(100% - ${DRAWER_WIDTH} - ${DRAWER_WIDTH})`
                                : `0 0 calc(100% - ${DRAWER_WIDTH})`,
                            height: '100%',
                            overflowY: 'auto',
                            transition: theme.transitions.create('all', {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.leavingScreen,
                            }),
                        }}
                    >
                        <Box
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Container role="main" maxWidth="lg" sx={{ pb: 2 }}>
                                {children}
                            </Container>
                            <Box
                                component="footer"
                                sx={{
                                    paddingY: 2,
                                    width: '100%',
                                }}
                            >
                                <Copyright />
                            </Box>
                        </Box>
                    </Box>
                    {/** Drawer Container */}
                    {/** Do not show if user is not logged in */}
                    {session && (
                        <Box sx={{ overflowY: 'auto', height: '100%', flex: `0 0 ${DRAWER_WIDTH}` }}>
                            <Drawer />
                        </Box>
                    )}
                </Box>
            </Box>
        </EnvContext.Provider>
    );
}
